import {
  Button,
  makeStyles,
  TextField,
  Box,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import Autocomplete from "@mui/material/Autocomplete";
import MuiTextField from "@mui/material/TextField";
import React, { useState, useEffect } from "react";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import useToggle from "../../../state/useToggle";
import { toast } from "react-toastify";
import api from "../../../api";
import { hasPms } from "../../../utils/partnerPms";

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
  },
  buttonContainer: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
}));

export default function LedgerConfigurationModal({ partnerId, partner, saveAttribute }) {
  const classes = useStyles();
  const toggle = useToggle();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    chargeCode: "",
    amount: "",
    pmsRecurringCharge: false,
    pmsRecurringChargeCreationDay: "",
    pmsRecurringChargeDueDay: "",
    pmsChargePostOnApproval: false,
    pmsChargePostOnApprovalDay: "",
  });

  const [errors, setErrors] = useState({
    pmsRecurringChargeCreationDay: "",
    pmsRecurringChargeDueDay: "",
    pmsChargePostOnApprovalDay: "",
  });

  const [glAccounts, setGlAccounts] = useState([]);
  const [glAccountsLoading, setGlAccountsLoading] = useState(false);
  const [selectedGlAccount, setSelectedGlAccount] = useState(null);

  const isAppfolio = hasPms(partner, "appfolio");

  useEffect(() => {
    if (toggle.on && partner) {
      setFormData({
        chargeCode: partner.pmsChargeCode || "",
        amount: partner.pmsAmount?.cents
          ? (partner.pmsAmount.cents / 100).toString()
          : "",
        pmsRecurringCharge: partner.pmsRecurringCharge || false,
        pmsRecurringChargeCreationDay: partner.pmsRecurringChargeCreationDay || "",
        pmsRecurringChargeDueDay: partner.pmsRecurringChargeDueDay || "",
        pmsChargePostOnApproval: partner.pmsChargePostOnApproval || false,
        pmsChargePostOnApprovalDay: partner.pmsChargePostOnApprovalDay || "",
      });
      setErrors({
        pmsRecurringChargeCreationDay: "",
        pmsRecurringChargeDueDay: "",
        pmsChargePostOnApprovalDay: "",
      });
      setIsSubmitting(false);

      if (isAppfolio) {
        setGlAccountsLoading(true);
        api
          .getAppfolioGlAccounts(partnerId)
          .then((resp) => {
            const accounts = resp.data.glAccounts || [];
            setGlAccounts(accounts);
            const currentCode = partner.pmsChargeCode;
            if (currentCode) {
              const match = accounts.find((a) => a.id === currentCode);
              setSelectedGlAccount(match || null);
            } else {
              setSelectedGlAccount(null);
            }
          })
          .catch(() => {
            toast.error("Failed to load GL accounts");
            setGlAccounts([]);
            setSelectedGlAccount(null);
          })
          .finally(() => setGlAccountsLoading(false));
      }
    }
  }, [toggle.on, partner, isAppfolio, partnerId]);

  const validateDay = (value, fieldName) => {
    if (!value) {
      return "";
    }

    const day = parseInt(value, 10);

    if (isNaN(day)) {
      return "Must be a valid number";
    }

    if (day < 1 || day > 31) {
      return "Must be between 1 and 31";
    }

    if (!Number.isInteger(parseFloat(value))) {
      return "Must be a whole number";
    }

    return "";
  };

  const handleDayBlur = (fieldName) => {
    const value = formData[fieldName];
    const error = validateDay(value, fieldName);
    setErrors({ ...errors, [fieldName]: error });
  };

  const handleDayChange = (fieldName, value) => {
    setFormData({ ...formData, [fieldName]: value });
    if (errors[fieldName]) {
      const error = validateDay(value, fieldName);
      setErrors({ ...errors, [fieldName]: error });
    }
  };

  const handleSave = async () => {
    setIsSubmitting(true);

    try {
      const amountCents = formData.amount
        ? Math.round(parseFloat(formData.amount) * 100)
        : null;

      await saveAttribute({
        pms_charge_code: formData.chargeCode || null,
        pms_amount: amountCents ? { cents: amountCents, currency: "USD" } : null,
        pms_recurring_charge: formData.pmsRecurringCharge || null,
        pms_recurring_charge_creation_day: formData.pmsRecurringChargeCreationDay
          ? parseInt(formData.pmsRecurringChargeCreationDay, 10)
          : null,
        pms_recurring_charge_due_day: formData.pmsRecurringChargeDueDay
          ? parseInt(formData.pmsRecurringChargeDueDay, 10)
          : null,
        pms_charge_post_on_approval: formData.pmsChargePostOnApproval || null,
        pms_charge_post_on_approval_day: formData.pmsChargePostOnApprovalDay
          ? parseInt(formData.pmsChargePostOnApprovalDay, 10)
          : null,
      });

      toast.success("Ledger configuration saved successfully");
      toggle.toggle(); // Close modal
    } catch {
      // Error toast already shown by saveAttribute
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      chargeCode: partner.pmsChargeCode || "",
      amount: partner.pmsAmount?.cents ? (partner.pmsAmount.cents / 100).toString() : "",
      pmsRecurringCharge: partner.pmsRecurringCharge || false,
      pmsRecurringChargeCreationDay: partner.pmsRecurringChargeCreationDay || "",
      pmsRecurringChargeDueDay: partner.pmsRecurringChargeDueDay || "",
      pmsChargePostOnApproval: partner.pmsChargePostOnApproval || false,
      pmsChargePostOnApprovalDay: partner.pmsChargePostOnApprovalDay || "",
    });
    if (isAppfolio) {
      const currentCode = partner.pmsChargeCode;
      const match = glAccounts.find((a) => a.id === currentCode);
      setSelectedGlAccount(match || null);
    }
  };

  const isFormValid =
    formData.chargeCode &&
    formData.amount &&
    parseFloat(formData.amount) > 0 &&
    (!formData.pmsRecurringCharge ||
      (formData.pmsRecurringChargeCreationDay && formData.pmsRecurringChargeDueDay)) &&
    !errors.pmsRecurringChargeCreationDay &&
    !errors.pmsRecurringChargeDueDay &&
    !errors.pmsChargePostOnApprovalDay;

  return (
    <>
      <Button variant="outlined" onClick={toggle.toggle}>
        Ledger Configuration
      </Button>

      <ConfirmationDialog
        title="Ledger Configuration"
        open={toggle.on}
        onCancel={toggle.toggle}
        onConfirm={handleSave}
        text="Configure default charge settings for ledger operations. These values will be used as defaults when posting rent reporting charges to PMS."
        cancelProps={{ children: "Cancel" }}
        confirmProps={{
          children: isSubmitting ? <CircularProgress size={20} /> : "Save Configuration",
          disabled: !isFormValid || isSubmitting,
        }}
        maxWidth="sm"
        fullWidth
      >
        <Box className={classes.container}>
          <Box display="flex" flexDirection="column" gap={2.5}>
            {isAppfolio ? (
              <Autocomplete
                options={glAccounts}
                getOptionLabel={(option) => option.name || ""}
                value={selectedGlAccount}
                onChange={(e, newValue) => {
                  setSelectedGlAccount(newValue);
                  setFormData({
                    ...formData,
                    chargeCode: newValue ? newValue.id : "",
                  });
                }}
                loading={glAccountsLoading}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderInput={(params) => (
                  <MuiTextField
                    {...params}
                    label="GL Account"
                    required
                    helperText="Select the GL account for AppFolio charges"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {glAccountsLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            ) : (
              <TextField
                label="Charge Code or Name"
                value={formData.chargeCode}
                onChange={(e) => setFormData({ ...formData, chargeCode: e.target.value })}
                placeholder="e.g. RENT_REPORT"
                helperText="Code or name used to identify the charge type in PMS"
                required
                fullWidth
              />
            )}

            <TextField
              label="Amount (USD)"
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              placeholder="e.g. 29.99"
              helperText="Default amount to charge for rent reporting"
              required
              fullWidth
              inputProps={{ step: "0.01", min: "0" }}
            />

            <Box mt={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.pmsRecurringCharge}
                    onChange={(e) =>
                      setFormData({ ...formData, pmsRecurringCharge: e.target.checked })
                    }
                    color="primary"
                  />
                }
                label="Enable Recurring Charge"
              />
            </Box>

            <Box mt={1}>
              <TextField
                label="Recurring Charge Creation Day"
                type="number"
                value={formData.pmsRecurringChargeCreationDay}
                onChange={(e) =>
                  handleDayChange("pmsRecurringChargeCreationDay", e.target.value)
                }
                onBlur={() => handleDayBlur("pmsRecurringChargeCreationDay")}
                placeholder="1-31"
                helperText={
                  errors.pmsRecurringChargeCreationDay ||
                  "Day of the month Boom creates the charge and posts it to the resident ledger in the PMS for the upcoming month (1-31). If the month doesn't have this day (e.g., Feb 31), it will process on the last day of the month."
                }
                error={!!errors.pmsRecurringChargeCreationDay}
                disabled={!formData.pmsRecurringCharge}
                required={formData.pmsRecurringCharge}
                fullWidth
                inputProps={{ min: "1", max: "31" }}
              />
            </Box>

            <Box mt={1} mb={1}>
              <TextField
                label="Recurring Charge Due Date"
                type="number"
                value={formData.pmsRecurringChargeDueDay}
                onChange={(e) =>
                  handleDayChange("pmsRecurringChargeDueDay", e.target.value)
                }
                onBlur={() => handleDayBlur("pmsRecurringChargeDueDay")}
                placeholder="1-31"
                helperText={
                  errors.pmsRecurringChargeDueDay ||
                  "Day of the upcoming month that Boom will set the date of the charge (1-31). If the month doesn't have this day (e.g., Feb 31), it will be set to the last day of the month."
                }
                error={!!errors.pmsRecurringChargeDueDay}
                disabled={!formData.pmsRecurringCharge}
                required={formData.pmsRecurringCharge}
                fullWidth
                inputProps={{ min: "1", max: "31" }}
              />
            </Box>

            <Box mt={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.pmsChargePostOnApproval}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        pmsChargePostOnApproval: e.target.checked,
                      })
                    }
                    color="primary"
                  />
                }
                label="Post Charge on Approval"
              />
            </Box>

            <Box mt={1}>
              <TextField
                label="Post on Approval Day"
                type="number"
                value={formData.pmsChargePostOnApprovalDay}
                onChange={(e) =>
                  handleDayChange("pmsChargePostOnApprovalDay", e.target.value)
                }
                onBlur={() => handleDayBlur("pmsChargePostOnApprovalDay")}
                placeholder="1-31"
                helperText={
                  errors.pmsChargePostOnApprovalDay ||
                  "Optional: Day of month to post charge on approval (1-31). If not specified, the charge will post on approval date. If specified, the charge will post on this day in the upcoming month."
                }
                error={!!errors.pmsChargePostOnApprovalDay}
                disabled={!formData.pmsChargePostOnApproval}
                fullWidth
                inputProps={{ min: "1", max: "31" }}
              />
            </Box>

            <Box className={classes.buttonContainer}>
              <Button variant="outlined" onClick={handleReset}>
                Reset
              </Button>
            </Box>
          </Box>
        </Box>
      </ConfirmationDialog>
    </>
  );
}
